import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { ListItemText, MenuItem } from '@mui/material';
import Divider from '@mui/material/Divider';
import { Add as AddIcon } from '@mui/icons-material';

import { fetchActionCenters } from 'store/actions/campaigns';
import { organizationSelectors } from 'store/selectors/organizations';

import ButtonMenu from 'common/components/ButtonMenu';

/**
 * "New Message" `ButtonMenu` combining both messaging features: email/text
 * broadcast items that navigate straight to the creation pages, and email/
 * text action-alert items that defer to `alertDialogHandler` (fetching the
 * org's live action centers when the menu opens). Items are gated by the
 * `showBroadcasts` / `showTexting` / `showActionAlerts` flags.
 */
const CreateMessageButton = ({ alertDialogHandler }) => {
  const { organizationUuid } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    organization: { flags },
  } = useSelector(state => ({
    organization: organizationSelectors.getOrganization(state),
  }));

  const handleCreateBroadcast = type => {
    history.push(`/${organizationUuid}/create-${type}-broadcast/`);
  };

  const handleMenuOpen = () => {
    dispatch(
      fetchActionCenters({
        organization: organizationUuid,
        status: 'live',
      })
    );
  };

  return (
    <ButtonMenu
      name="New Message"
      disabled={!flags?.showBroadcasts}
      color="primary"
      onOpen={handleMenuOpen}
      startIcon={<AddIcon />}
      variant="contained"
    >
      <MenuItem onClick={() => handleCreateBroadcast('email')}>
        <ListItemText primary="Email Broadcast" />
      </MenuItem>
      <MenuItem disabled={!flags?.showTexting} onClick={() => handleCreateBroadcast('text')}>
        <ListItemText primary="Text Broadcast" />
      </MenuItem>
      <Divider />
      <MenuItem disabled={!flags?.showActionAlerts} onClick={() => alertDialogHandler('email')}>
        <ListItemText primary="Email Action Alert" />
      </MenuItem>
      <MenuItem
        disabled={!flags?.showActionAlerts || !flags?.showTexting}
        onClick={() => alertDialogHandler('text')}
      >
        <ListItemText primary="Text Action Alert" />
      </MenuItem>
    </ButtonMenu>
  );
};

CreateMessageButton.propTypes = {
  /** Called with the alert channel ('email' or 'text') when an action-alert item is chosen. */
  alertDialogHandler: PropTypes.func.isRequired,
};

export default CreateMessageButton;
